import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Hammer, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import { ListPagination } from '@/components/efs/ListPagination';
import { useBatchContext } from '@/app/BatchContext';
import { useWarehousesList } from '@/features/masters/warehouses/useWarehouses';
import { useBatchFacilityId } from '@/features/operations/inbound/useInbound';

import { useBulkRouteAssign, useBulkSortingWarehouse, useSortingPackages } from './useSorting';
import {
  getSortingAllowedActions,
  getSortingProcessingState,
  routeTypeValues,
  sortingProcessingStateValues,
  type RouteType,
  type SortingPackageFilters,
  type SortingPackageDto,
  type SortingProcessingState,
} from './types';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100] as const;
const NS = 'operations';
const ALL = '__all__';
const RECENT_REASON_KEY = 'efs.sorting.recentChangeReasons';
const MAX_RECENT_REASONS = 5;

// The filters live in the URL so the 解体 page can send the operator back to
// exactly the view they left. Defaults are omitted to keep the link readable.
const PARAM_ROUTE = 'route';
const PARAM_WAREHOUSE = 'warehouse';
const PARAM_STATE = 'state';
const PARAM_STATE_ALL = 'all';
const PARAM_PAGE = 'page';
const PARAM_PER_PAGE = 'per_page';
const DEFAULT_PROCESSING_FILTER: SortingProcessingState = 'available';

const readPageParam = (raw: string | null): number => {
  const parsed = Number.parseInt(raw ?? '', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const readPerPageParam = (raw: string | null): number => (raw === '100' ? 100 : DEFAULT_PER_PAGE);

const readRouteParam = (raw: string | null): RouteType | typeof ALL =>
  (routeTypeValues as readonly string[]).includes(raw ?? '') ? (raw as RouteType) : ALL;

const readProcessingParam = (raw: string | null): SortingProcessingState | typeof ALL => {
  if (raw === PARAM_STATE_ALL) return ALL;
  return (sortingProcessingStateValues as readonly string[]).includes(raw ?? '')
    ? (raw as SortingProcessingState)
    : DEFAULT_PROCESSING_FILTER;
};

const readRecentReasons = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_REASON_KEY) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string')
      : [];
  } catch {
    return [];
  }
};

const rememberReason = (reason: string, current: string[]): string[] => {
  const trimmed = reason.trim();
  if (!trimmed) return current;
  const next = [trimmed, ...current.filter((item) => item !== trimmed)].slice(
    0,
    MAX_RECENT_REASONS,
  );
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.setItem(RECENT_REASON_KEY, JSON.stringify(next));
    } catch {
      // Local history is only a convenience; the mutation itself must not fail.
    }
  }
  return next;
};

export const SortingListPage = () => {
  const { t } = useTranslation(NS);
  const params = useParams<{ batchId?: string }>();
  const { batchId: ctxBatchId } = useBatchContext();
  const batchId = params.batchId ?? ctxBatchId ?? undefined;

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [routeFilter, setRouteFilter] = useState<RouteType | typeof ALL>(() =>
    readRouteParam(searchParams.get(PARAM_ROUTE)),
  );
  const [warehouseFilter, setWarehouseFilter] = useState<string>(
    () => searchParams.get(PARAM_WAREHOUSE) ?? ALL,
  );
  const [processingFilter, setProcessingFilter] = useState<SortingProcessingState | typeof ALL>(
    () => readProcessingParam(searchParams.get(PARAM_STATE)),
  );
  const [page, setPage] = useState(() => readPageParam(searchParams.get(PARAM_PAGE)));
  const [perPage, setPerPage] = useState(() => readPerPageParam(searchParams.get(PARAM_PER_PAGE)));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reason-prompt modal — opened when at least one selected package needs
  // an audit reason for the proposed change. Holds the pending mutation
  // payload so the confirm button can apply it after the operator types
  // the reason.
  const [reasonModal, setReasonModal] = useState<{
    open: boolean;
    kind: 'route' | 'warehouse';
    label: string;
    routeType: RouteType | null;
    warehouseId: string | null;
    affectedCount: number;
    /** Packages the mutation applies to — the current selection, or the single
     *  row / hand-off set that opened the modal. */
    targets: SortingPackageDto[];
    /** Set when the route change is only a step on the way to the 解体 page. */
    handoffHref: string | null;
  }>({
    open: false,
    kind: 'route',
    label: '',
    routeType: null,
    warehouseId: null,
    affectedCount: 0,
    targets: [],
    handoffHref: null,
  });
  const [reasonText, setReasonText] = useState('');
  /** Message from a refused bulk mutation, shown inside the reason modal. */
  const [saveError, setSaveError] = useState<string | null>(null);
  const [recentReasons, setRecentReasons] = useState<string[]>(() => readRecentReasons());

  // Scope to the batch's facility: bulk-warehouse only accepts a warehouse
  // from that facility, so other facilities' warehouses would always fail.
  const batchFacilityId = useBatchFacilityId(batchId);
  const warehousesQuery = useWarehousesList(
    { active_only: true, per_page: 500, facility_id: batchFacilityId },
    { enabled: !!batchFacilityId },
  );
  const warehouses = warehousesQuery.data?.rows ?? [];
  const warehouseOptions = useMemo(
    () =>
      (warehousesQuery.data?.rows ?? []).map((wh) => ({
        value: wh.warehouse_id,
        label: `${wh.code} — ${wh.display_name}`,
      })),
    [warehousesQuery.data?.rows],
  );

  const filters = useMemo<SortingPackageFilters>(
    () => ({
      batch_id: batchId,
      route_type: routeFilter === ALL ? undefined : routeFilter,
      warehouse_id: warehouseFilter === ALL ? undefined : warehouseFilter,
      processing_state: processingFilter === ALL ? 'all' : processingFilter,
      page,
      per_page: perPage,
    }),
    [batchId, page, perPage, processingFilter, routeFilter, warehouseFilter],
  );

  const query = useSortingPackages(filters);
  const rows = useMemo(() => query.data?.data ?? [], [query.data?.data]);
  const total = query.data?.meta?.total ?? 0;
  const selectableRows = useMemo(
    () =>
      rows.filter((pkg) => {
        if (getSortingProcessingState(pkg) !== 'available') return false;
        const actions = getSortingAllowedActions(pkg);
        return actions.includes('route') || actions.includes('change_warehouse');
      }),
    [rows],
  );
  const selectedRows = useMemo(
    () => selectableRows.filter((pkg) => selectedIds.has(pkg.inbound_package_id)),
    [selectableRows, selectedIds],
  );
  const allSelectableSelected =
    selectableRows.length > 0 &&
    selectableRows.every((pkg) => selectedIds.has(pkg.inbound_package_id));

  const bulkRoute = useBulkRouteAssign();
  const bulkWarehouse = useBulkSortingWarehouse();

  const routeLabel = (rt: string | null | undefined): string =>
    rt ? t(`efs.masters.route_type.${rt}`, { ns: 'masters', defaultValue: rt }) : '—';

  useEffect(() => {
    setSelectedIds((previous) => {
      const allowed = new Set(selectableRows.map((pkg) => pkg.inbound_package_id));
      const next = new Set(Array.from(previous).filter((id) => allowed.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [selectableRows]);

  const togglePkgSelected = (pkg: SortingPackageDto) => {
    if (!selectableRows.some((row) => row.inbound_package_id === pkg.inbound_package_id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(pkg.inbound_package_id)) next.delete(pkg.inbound_package_id);
      else next.add(pkg.inbound_package_id);
      return next;
    });
  };

  const routePackages = async (
    targets: SortingPackageDto[],
    rt: RouteType,
    change_reason?: string,
  ): Promise<boolean> => {
    const packageIds = targets
      .filter((pkg) => getSortingAllowedActions(pkg).includes('route'))
      .map((pkg) => pkg.inbound_package_id);
    if (packageIds.length === 0) return false;
    await bulkRoute.mutateAsync({
      package_ids: packageIds,
      route_type: rt,
      change_reason,
    });
    return true;
  };

  const runBulkRoute = async (rt: RouteType, change_reason?: string) => {
    if (await routePackages(selectedRows, rt, change_reason)) {
      setSelectedIds(new Set());
    }
  };

  const runBulkWarehouse = async (warehouseId: string | null, change_reason?: string) => {
    const packageIds = selectedRows
      .filter((pkg) => getSortingAllowedActions(pkg).includes('change_warehouse'))
      .map((pkg) => pkg.inbound_package_id);
    if (packageIds.length === 0) return;
    await bulkWarehouse.mutateAsync({
      package_ids: packageIds,
      warehouse_id: warehouseId,
      change_reason,
    });
    setSelectedIds(new Set());
  };

  /**
   * The 解体 page resolves `packages` server-side, so the hand-off can name
   * pallets the dismantling list window would never show. `from` carries this
   * page's own query string back so 「仕分けへ戻る」 restores the view.
   */
  const dismantlingHandoffHref = (packageIds: string[]): string | null => {
    if (!batchId || packageIds.length === 0) return null;
    const from = location.search.replace(/^\?/, '');
    const query = [`packages=${packageIds.join(',')}`];
    if (from) query.push(`from=${encodeURIComponent(from)}`);
    return `/batches/${batchId}/dismantling?${query.join('&')}`;
  };

  const isHandoffEligible = (pkg: SortingPackageDto): boolean => {
    const actions = getSortingAllowedActions(pkg);
    return actions.includes('start_dismantling') || actions.includes('route_and_dismantle');
  };

  const needsRoutingForDismantling = (pkg: SortingPackageDto): boolean =>
    getSortingAllowedActions(pkg).includes('route_and_dismantle');

  /**
   * Hands N pallets to the 解体 page in one move. Pallets routed elsewhere are
   * switched to manual_dismantling first — through the same audit-reason modal
   * as any other route change, because inbound confirmation always leaves a
   * non-null route_type behind.
   */
  const startDismantlingHandoff = async (targets: SortingPackageDto[]) => {
    const eligible = targets.filter(isHandoffEligible);
    const href = dismantlingHandoffHref(eligible.map((pkg) => pkg.inbound_package_id));
    if (!href) return;
    if (!eligible.some(needsRoutingForDismantling)) {
      navigate(href);
      return;
    }
    // Only pallets that are NOT yet on a dismantling route get rerouted. A
    // legacy manual_heavy_dismantling pallet is already valid input for a
    // session, so rewriting it to manual_dismantling would throw away a real
    // classification and log a pointless audit revision — it still travels in
    // the hand-off URL, it just is not touched.
    const routable = eligible.filter(
      (pkg) => needsRoutingForDismantling(pkg) && getSortingAllowedActions(pkg).includes('route'),
    );
    const affected = routable.filter(
      (pkg) => (pkg.route_type ?? null) != null && pkg.route_type !== 'manual_dismantling',
    );
    if (affected.length > 0) {
      setReasonText(recentReasons[0] ?? '');
      setSaveError(null);
      setReasonModal({
        open: true,
        kind: 'route',
        label: routeLabel('manual_dismantling'),
        routeType: 'manual_dismantling',
        warehouseId: null,
        affectedCount: affected.length,
        targets: routable,
        handoffHref: href,
      });
      return;
    }
    await routePackages(routable, 'manual_dismantling');
    navigate(href);
  };

  const handleWarehouseClick = async (warehouseId: string | null, label: string) => {
    const selected = selectedRows.filter((pkg) =>
      getSortingAllowedActions(pkg).includes('change_warehouse'),
    );
    const affected = selected.filter(
      (p) => p.status === 'completed' && (p.warehouse_id ?? null) !== warehouseId,
    );
    if (affected.length > 0) {
      setReasonText(recentReasons[0] ?? '');
      setReasonModal({
        open: true,
        kind: 'warehouse',
        label,
        routeType: null,
        warehouseId,
        affectedCount: affected.length,
        targets: selected,
        handoffHref: null,
      });
      return;
    }
    await runBulkWarehouse(warehouseId);
  };

  const formatWarehouse = (p: SortingPackageDto): string => {
    if (!p.warehouse_id) return '—';
    if (p.warehouse_code && p.warehouse_display_name) {
      return `${p.warehouse_code} — ${p.warehouse_display_name}`;
    }
    return p.warehouse_code ?? p.warehouse_display_name ?? p.warehouse_id.slice(0, 8) + '…';
  };

  const syncFilterParam = (key: string, value: string | null) => {
    setPage(1);
    setSelectedIds(new Set());
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value == null) next.delete(key);
        else next.set(key, value);
        next.delete(PARAM_PAGE);
        return next;
      },
      { replace: true },
    );
  };

  const changePage = (nextPage: number) => {
    const normalized = Math.max(1, nextPage);
    setPage(normalized);
    setSelectedIds(new Set());
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (normalized === 1) next.delete(PARAM_PAGE);
        else next.set(PARAM_PAGE, String(normalized));
        return next;
      },
      { replace: true },
    );
  };

  const changePerPage = (nextPerPage: number) => {
    const normalized = nextPerPage === 100 ? 100 : DEFAULT_PER_PAGE;
    setPerPage(normalized);
    setPage(1);
    setSelectedIds(new Set());
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(PARAM_PAGE);
        if (normalized === DEFAULT_PER_PAGE) next.delete(PARAM_PER_PAGE);
        else next.set(PARAM_PER_PAGE, String(normalized));
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (!query.data || query.isPlaceholderData) return;
    const lastPage = Math.max(1, Math.ceil(total / perPage));
    if (page <= lastPage) return;

    setPage(lastPage);
    setSelectedIds(new Set());
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (lastPage === 1) next.delete(PARAM_PAGE);
        else next.set(PARAM_PAGE, String(lastPage));
        return next;
      },
      { replace: true },
    );
  }, [page, perPage, query.data, query.isPlaceholderData, setSearchParams, total]);

  const changeRouteFilter = (value: string) => {
    setRouteFilter(value as RouteType | typeof ALL);
    syncFilterParam(PARAM_ROUTE, value === ALL ? null : value);
  };

  const changeWarehouseFilter = (value: string) => {
    setWarehouseFilter(value);
    syncFilterParam(PARAM_WAREHOUSE, value === ALL ? null : value);
  };

  const changeProcessingFilter = (value: string) => {
    setProcessingFilter(value as SortingProcessingState | typeof ALL);
    syncFilterParam(
      PARAM_STATE,
      value === DEFAULT_PROCESSING_FILTER ? null : value === ALL ? PARAM_STATE_ALL : value,
    );
  };

  const processingLabel = (state: SortingProcessingState): string =>
    t(`efs.operations.sorting.processing_states.${state}`);

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.operations.sorting.title')}
        subtitle={t('efs.operations.sorting.subtitle')}
        count={total}
      />

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-efs-border bg-efs-bg-panel p-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-efs-text-muted">
            {t('efs.operations.sorting.fields.route_type')}
          </label>
          <Select value={routeFilter} onValueChange={changeRouteFilter}>
            <SelectTrigger className="h-8 w-40 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t('efs.operations.sorting.filters.all_routes')}</SelectItem>
              {routeTypeValues.map((rt) => (
                <SelectItem key={rt} value={rt}>
                  {routeLabel(rt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-efs-text-muted">
            {t('efs.operations.sorting.fields.warehouse')}
          </label>
          <div className="w-56">
            <SearchableSelect
              options={warehouseOptions}
              value={warehouseFilter === ALL ? '' : warehouseFilter}
              onChange={(value) => changeWarehouseFilter(value === '' ? ALL : value)}
              placeholder={t('efs.operations.sorting.filters.all_warehouses')}
              clearLabel={t('efs.operations.sorting.filters.all_warehouses')}
              className="h-8 text-xs"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-efs-text-muted">
            {t('efs.operations.sorting.fields.processing_state')}
          </label>
          <Select value={processingFilter} onValueChange={changeProcessingFilter}>
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>
                {t('efs.operations.sorting.filters.all_processing_states')}
              </SelectItem>
              {sortingProcessingStateValues.map((state) => (
                <SelectItem key={state} value={state}>
                  {processingLabel(state)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border border-efs-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <input
                  type="checkbox"
                  aria-label={t('efs.operations.sorting.bulk_assign.selected', {
                    count: rows.length,
                  })}
                  checked={allSelectableSelected}
                  disabled={selectableRows.length === 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(new Set(selectableRows.map((pkg) => pkg.inbound_package_id)));
                    } else {
                      setSelectedIds(new Set());
                    }
                  }}
                />
              </TableHead>
              <TableHead>{t('efs.operations.sorting.fields.package_code')}</TableHead>
              <TableHead>{t('efs.operations.sorting.fields.item_name')}</TableHead>
              <TableHead>{t('efs.operations.sorting.fields.warehouse')}</TableHead>
              <TableHead>{t('efs.operations.sorting.fields.inbound_date')}</TableHead>
              <TableHead>{t('efs.operations.sorting.fields.route_type')}</TableHead>
              <TableHead>{t('efs.operations.sorting.fields.processing_state')}</TableHead>
              <TableHead className="text-right">
                {t('efs.operations.sorting.fields.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-efs-text-muted">
                  {t('efs.common.messages.loading', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-efs-text-muted">
                  {t('efs.common.messages.empty', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((pkg) => {
                const processingState = getSortingProcessingState(pkg);
                const allowedActions = getSortingAllowedActions(pkg);
                const selectable = selectableRows.some(
                  (row) => row.inbound_package_id === pkg.inbound_package_id,
                );
                const handoffHref = dismantlingHandoffHref([pkg.inbound_package_id]);
                // Every action addresses the PALLET, including 続ける / 履歴:
                // the package id resolves server-side (with its session), while
                // ?session= could only be matched inside the dismantling page's
                // own 100-row window and silently opened nothing outside it.
                const sessionHref = handoffHref;
                return (
                  <TableRow key={pkg.inbound_package_id}>
                    <TableCell className="w-8">
                      {selectable ? (
                        <input
                          type="checkbox"
                          aria-label={pkg.package_code}
                          checked={selectedIds.has(pkg.inbound_package_id)}
                          onChange={() => togglePkgSelected(pkg)}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{pkg.package_code}</TableCell>
                    <TableCell className="text-xs">{pkg.incoming_item_name}</TableCell>
                    <TableCell className="text-xs">{formatWarehouse(pkg)}</TableCell>
                    <TableCell className="text-xs">{pkg.inbound_date}</TableCell>
                    <TableCell className="text-xs">{routeLabel(pkg.route_type)}</TableCell>
                    <TableCell>
                      <Badge variant={processingState === 'available' ? 'outline' : 'secondary'}>
                        {processingLabel(processingState)}
                        {processingState === 'dismantling_in_progress' &&
                        pkg.dismantling_progress_percent != null
                          ? ` ${pkg.dismantling_progress_percent.toFixed(2)}%`
                          : ''}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {sessionHref && allowedActions.includes('continue_dismantling') ? (
                        <Button asChild size="sm" variant="outline">
                          <Link to={sessionHref}>
                            {t('efs.operations.sorting.actions.continue_dismantling')}
                          </Link>
                        </Button>
                      ) : sessionHref && allowedActions.includes('view_history') ? (
                        <Button asChild size="sm" variant="outline">
                          <Link to={sessionHref}>
                            {t('efs.operations.sorting.actions.view_history')}
                          </Link>
                        </Button>
                      ) : handoffHref && allowedActions.includes('start_dismantling') ? (
                        <Button asChild size="sm" variant="outline">
                          <Link to={handoffHref}>
                            {t('efs.operations.sorting.actions.start_dismantling')}
                          </Link>
                        </Button>
                      ) : handoffHref && allowedActions.includes('route_and_dismantle') ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={bulkRoute.isPending}
                          onClick={() => void startDismantlingHandoff([pkg])}
                        >
                          {t('efs.operations.sorting.actions.start_dismantling')}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <ListPagination
        page={page}
        perPage={perPage}
        total={total}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        onPageChange={changePage}
        onPerPageChange={changePerPage}
      />

      {/* Reason modal — opened when audit reason required. */}
      <Dialog
        open={reasonModal.open}
        onOpenChange={(v) => setReasonModal((m) => ({ ...m, open: v }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('efs.operations.sorting.bulk_assign.reason_title')}</DialogTitle>
            <DialogDescription>
              {reasonModal.kind === 'route'
                ? t('efs.operations.sorting.bulk_assign.reason_route_subtitle', {
                    count: reasonModal.affectedCount,
                    label: reasonModal.label,
                  })
                : t('efs.operations.sorting.bulk_assign.reason_warehouse_subtitle', {
                    count: reasonModal.affectedCount,
                    label: reasonModal.label,
                  })}
            </DialogDescription>
          </DialogHeader>
          {recentReasons.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-efs-text-muted">
                {t('efs.operations.sorting.bulk_assign.recent_reasons')}
              </p>
              <div className="flex flex-wrap gap-2">
                {recentReasons.map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    className="max-w-full truncate rounded-md border border-efs-border bg-efs-bg-subtle px-2.5 py-1 text-xs text-efs-text-primary hover:bg-efs-bg-panel"
                    onClick={() => setReasonText(reason)}
                    title={reason}
                  >
                    {reason}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <Textarea
            rows={3}
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            placeholder={t('efs.operations.sorting.bulk_assign.reason_placeholder')}
            autoFocus
          />
          {saveError ? (
            <p
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100"
            >
              {t('efs.operations.sorting.errors.save_failed', { message: saveError })}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setReasonModal((m) => ({ ...m, open: false }))}
              disabled={bulkRoute.isPending || bulkWarehouse.isPending}
            >
              {t('efs.common.actions.cancel', { ns: 'common' })}
            </Button>
            <Button
              disabled={bulkRoute.isPending || bulkWarehouse.isPending || !reasonText.trim()}
              onClick={async () => {
                const reason = reasonText.trim();
                setSaveError(null);
                try {
                  if (reasonModal.kind === 'route' && reasonModal.routeType) {
                    if (reasonModal.handoffHref) {
                      await routePackages(reasonModal.targets, reasonModal.routeType, reason);
                    } else {
                      await runBulkRoute(reasonModal.routeType, reason);
                    }
                  } else if (reasonModal.kind === 'warehouse') {
                    await runBulkWarehouse(reasonModal.warehouseId, reason);
                  }
                } catch (error) {
                  // Refused (locked lot, pallet already reserved, …): stay in the
                  // modal with the typed reason and — above all — do not hand an
                  // unrouted pallet to the session page, which would reject it
                  // a second time with a less obvious message.
                  setSaveError(error instanceof Error ? error.message : String(error));
                  return;
                }
                setRecentReasons((prev) => rememberReason(reason, prev));
                setReasonModal((m) => ({ ...m, open: false }));
                if (reasonModal.handoffHref) navigate(reasonModal.handoffHref);
              }}
            >
              {bulkRoute.isPending || bulkWarehouse.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : null}
              {t('efs.operations.sorting.bulk_assign.confirm')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sticky bottom toolbar — same FAB pattern as InboundLotDetailPage. */}
      {selectedRows.length > 0 ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-4">
          <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-full border border-amber-300 bg-amber-50/95 px-4 py-2 text-sm shadow-lg backdrop-blur dark:border-amber-700 dark:bg-amber-950">
            <span className="font-semibold text-amber-900 dark:text-amber-100">
              {t('efs.operations.sorting.bulk_assign.selected', {
                count: selectedRows.length,
              })}
            </span>

            {/* One move for the real pain: the pallets picked here arrive on
                the 解体 page already selected, instead of being re-ticked. */}
            <Button
              size="sm"
              onClick={() => void startDismantlingHandoff(selectedRows)}
              disabled={
                bulkRoute.isPending ||
                selectedRows.length === 0 ||
                !selectedRows.every(isHandoffEligible)
              }
            >
              <Hammer className="mr-1 h-4 w-4" />
              {t('efs.operations.sorting.actions.start_dismantling_bulk')}
            </Button>

            {/* No per-route buttons: the action above re-routes to manual
                dismantling, asking for the audit reason when needed. */}
            <span className="mx-2 inline-block h-5 w-px bg-amber-400/60" aria-hidden />

            <span className="text-xs text-amber-900 dark:text-amber-100">
              {t('efs.operations.sorting.bulk_assign.warehouse_label')}
            </span>
            {/* One searchable picker instead of a button per warehouse — the
                toolbar stopped fitting once sites grew past a handful of
                warehouses (field report 2026-07-31). Picking fires the same
                bulk-assign flow the buttons did; clearing maps to 倉庫解除. */}
            <div className="w-64">
              <SearchableSelect
                options={warehouseOptions}
                value=""
                onChange={(warehouseId) => {
                  if (warehouseId === '') {
                    void handleWarehouseClick(
                      null,
                      t('efs.operations.sorting.bulk_assign.clear_warehouse'),
                    );
                    return;
                  }
                  const wh = warehouses.find((w) => w.warehouse_id === warehouseId);
                  if (wh) {
                    void handleWarehouseClick(wh.warehouse_id, `${wh.code} — ${wh.display_name}`);
                  }
                }}
                disabled={
                  bulkWarehouse.isPending ||
                  !selectedRows.some((pkg) =>
                    getSortingAllowedActions(pkg).includes('change_warehouse'),
                  )
                }
                placeholder={t('efs.operations.sorting.bulk_assign.warehouse_placeholder', {
                  defaultValue: '倉庫を選択…',
                })}
                clearLabel={t('efs.operations.sorting.bulk_assign.clear_warehouse')}
                className="h-8 bg-white text-xs dark:bg-transparent"
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkRoute.isPending || bulkWarehouse.isPending}
            >
              {t('efs.operations.sorting.bulk_assign.cancel')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// RowRouteMenu removed — all edits go through the checkbox bulk-toolbar.

export default SortingListPage;
